import { useSelector } from "react-redux";
import "./IncomeHandler.scss";
import { useInvoiceLines } from "./useInvoiceLines";
import { useL10n } from "../../../../../l10n";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import AddInvoiceLineRow from "./AddInvoiceLineRow";
import InvoiceLineEditingRow from "./EditInvoiceLineRow";
import { useTranslation } from "react-i18next";
import { DoraButton, DoraCheckbox } from "dora-shared/frontend";
import Decimal from "decimal.js-light";
import { faLock, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import ProductCellValue from "./ProductCellValue";
import UnitCellValue from "./UnitCellValue";
import { useDrag, useDrop } from "react-dnd";
import clsx from "clsx";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { selectFinanceConfiguration } from "../../../../../ducks/data/finance/selectors";
import { Tooltip } from "@mui/material";
import { useFeature } from "../../../../../hooks";
import { useAppDispatch } from "../../../../../redux-store";
import { selectRemoteLineChanges } from "../../../../../ducks/data/deliveries/selectors";
import { LineField } from "../../../../../ducks/data/deliveries/remote-line-changes";
import * as notificationActions from "../../../../../ducks/notifications";

const INVOICE_LINE = "INVOICE_LINE";

// How long the values someone else changed stay highlighted.
export const REMOTE_CHANGE_HIGHLIGHT_MS = 3000;

const NO_FIELDS: readonly LineField[] = [];

const IncomeHandler = (props: {
  deliveryId: string;
  selectedLineIds: string[];
  showAddNewLine: boolean;
  onCancelNewLine: () => void;
  onSelectedLinesChange: (lineIds: string[]) => void;
  onLinesCreated: (newLineIds: string[]) => void;
  clientId?: string;
  disabled: boolean;
  noCheckboxes: boolean;
  createNew?: boolean;
}) => {
  const { t } = useTranslation(["invoicing", "components"]);
  const {
    deliveryId,
    onSelectedLinesChange,
    selectedLineIds,
    showAddNewLine,
    onCancelNewLine,
    clientId,
    disabled,
    noCheckboxes,
    createNew,
  } = props;
  const {
    lines,
    addLine,
    updateLine,
    updateSurchargeLine,
    deleteLine,
    reorderLine,
  } = useInvoiceLines({ deliveryId, clientId, createNew });

  // Draft lines have no invoice/consolidation status yet, so the status column
  // is meaningless while creating a new cargo.
  const showStatusColumn =
    useFeature("edit-consolidated-invoice-lines-in-shipment-modal") &&
    !createNew;
  const [editingLineId, setEditingLineId] = useState<string | null>(null);

  // Someone else changed the line the user has open for editing: the edit row
  // would offer to save over their change, so it closes, and what they changed
  // is highlighted (or, if they deleted it, the user is told). The user's edit
  // is not kept; they open the line again on top of the new values.
  const dispatch = useAppDispatch();
  const remoteChanges = useSelector(selectRemoteLineChanges(deliveryId));
  const [highlight, setHighlight] = useState<{
    lineId: string;
    fields: readonly LineField[];
  } | null>(null);
  // Read when a change arrives, without re-running for each edit opened.
  const editingLineIdRef = useRef(editingLineId);
  editingLineIdRef.current = editingLineId;
  useEffect(() => {
    const lineId = editingLineIdRef.current;
    if (!remoteChanges || !lineId) {
      return;
    }
    if (remoteChanges.deleted.includes(lineId)) {
      setEditingLineId(null);
      dispatch(
        notificationActions.notifyL({
          namespace: "notifications",
          key: "invoiceLineDeletedElsewhere",
          type: "warning",
        })
      );
      return;
    }
    const fields = remoteChanges.changed[lineId];
    if (fields) {
      setEditingLineId(null);
      setHighlight({ lineId, fields });
    }
  }, [remoteChanges, dispatch]);
  useEffect(() => {
    if (!highlight) {
      return;
    }
    const timeout = setTimeout(
      () => setHighlight(null),
      REMOTE_CHANGE_HIGHLIGHT_MS
    );
    return () => clearTimeout(timeout);
  }, [highlight]);
  const lineViewModels = useMemo(
    () =>
      lines.map((line) => ({
        line,
        selected: selectedLineIds.includes(line.id),
        // A line invoiced while its edit row is open can no longer be
        // changed, so the row closes rather than offer a save that 409s.
        editing: editingLineId === line.id && !line.locked,
      })),
    [lines, selectedLineIds, editingLineId]
  );

  const selectableLines = useMemo(
    () => lines.filter((l) => !l.locked && l.deferredInvoiceInvoiced !== false),
    [lines]
  );

  const allLinesChecked = useMemo(
    () => selectedLineIds.length === selectableLines.length,
    [selectedLineIds, selectableLines]
  );

  const setSelected = (id: string, selected: boolean) => {
    const previousSelection = [...selectedLineIds];
    if (selected) {
      if (previousSelection.includes(id)) {
        return;
      }
      onSelectedLinesChange([...previousSelection, id]);
    } else {
      onSelectedLinesChange(previousSelection.filter((x) => x !== id));
    }
  };

  const onEditClick = (lineId: string) => {
    setEditingLineId(lineId);
  };

  const onAllCheckboxClick = (_v: boolean) => {
    if (allLinesChecked) {
      onSelectedLinesChange([]);
      return;
    }
    onSelectedLinesChange(selectableLines.map((x) => x.id));
  };

  const onAfterCreate = (newLineIds: string[]) => {
    props.onLinesCreated(newLineIds);
  };

  // Until the delete comes back, the table still shows the line, so a second
  // click (or deleting/editing another line) would race the delivery's
  // optimistic lock and come back as a 409 (DISPATCHER-8K). Block the row
  // actions while a delete is in flight.
  const deleteReqInProgressRef = useRef(false);
  const [deleteReqInProgress, setDeleteReqInProgress] = useState(false);
  const onDeleteClick = async (lineId: string) => {
    if (deleteReqInProgressRef.current) {
      return;
    }
    deleteReqInProgressRef.current = true;
    setDeleteReqInProgress(true);
    try {
      await deleteLine(lineId);
    } catch {
      // The deleteLine thunk has already reported it and shown the error toast.
    } finally {
      deleteReqInProgressRef.current = false;
      setDeleteReqInProgress(false);
    }
  };

  return (
    <>
      <div
        className={clsx("income-grid", {
          "income-grid--with-status": showStatusColumn,
          "income-grid--no-checkboxes": noCheckboxes,
        })}
        role="grid"
      >
        <div role="row" className="income-grid__row">
          {!noCheckboxes && (
            <div role="gridcell" className="income-grid__heading">
              <div className="income-grid-heading__checkbox">
                <DoraCheckbox
                  checked={allLinesChecked}
                  onChange={onAllCheckboxClick}
                />
              </div>
            </div>
          )}
          <div
            role="gridcell"
            className="income-grid__heading"
            id="income-grid-heading-product"
          >
            {t("invoicing:invoiceLine.code")}
          </div>
          <div role="gridcell" className="income-grid__heading">
            {t("invoicing:invoiceLine.description")}
          </div>
          <div
            role="gridcell"
            className="income-grid__heading flex justify-end"
          >
            {t("invoicing:invoiceLine.quantity")}
          </div>
          <div
            role="gridcell"
            className="income-grid__heading"
            id="income-grid-heading-unit"
          >
            {t("invoicing:invoiceLine.unit")}
          </div>
          <div
            role="gridcell"
            className="income-grid__heading flex justify-end"
          >
            {t("invoicing:invoiceLine.amount")}
          </div>
          <div
            role="gridcell"
            className="income-grid__heading flex justify-end"
            id="income-grid-heading-surcharge-percentage"
          >
            {t("invoicing:invoiceLine.surcharge")}
          </div>
          <div
            role="gridcell"
            className="income-grid__heading flex justify-end"
            id="income-grid-heading-discount-percentage"
          >
            {t("invoicing:invoiceLine.discount")}
          </div>
          <div role="gridcell" className="income-grid__heading">
            {t("invoicing:invoiceLine.created")}
          </div>
          <div
            role="gridcell"
            className="income-grid__heading flex justify-end"
          >
            {t("invoicing:invoiceLine.sum")}
          </div>
          {showStatusColumn && (
            <div role="gridcell" className="income-grid__heading">
              {t("invoicing:invoiceLine.status")}
            </div>
          )}
          <div role="gridcell" className="income-grid__heading"></div>
        </div>
        {lineViewModels.map(({ line, selected, editing }, i) => (
          <InvoiceLineWrapper
            key={line.id}
            line={line}
            selected={selected}
            editing={editing}
            highlightedFields={
              highlight?.lineId === line.id ? highlight.fields : NO_FIELDS
            }
            clientId={clientId}
            deliveryId={deliveryId}
            setEditingLineId={setEditingLineId}
            setSelected={setSelected}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            rowActionsDisabled={deleteReqInProgress}
            updateLine={updateLine}
            updateSurchargeLine={updateSurchargeLine}
            reorderLine={reorderLine}
            disabled={disabled}
            noCheckboxes={noCheckboxes}
            showStatusColumn={showStatusColumn}
            index={i}
          />
        ))}
        {showAddNewLine && (
          <AddInvoiceLineRow
            onCancel={onCancelNewLine}
            deliveryId={deliveryId}
            clientId={clientId}
            onAdd={addLine}
            onAfterCreate={onAfterCreate}
            noCheckboxes={noCheckboxes}
            showStatusColumn={showStatusColumn}
          />
        )}
      </div>
    </>
  );
};

const InvoiceLineWrapper = ({
  line,
  selected,
  editing,
  highlightedFields,
  deliveryId,
  clientId,
  setEditingLineId,
  setSelected,
  onEditClick,
  onDeleteClick,
  rowActionsDisabled,
  updateLine,
  updateSurchargeLine,
  reorderLine,
  index,
  disabled,
  noCheckboxes,
  showStatusColumn,
}: {
  line: any;
  selected: boolean;
  editing: boolean;
  // Values someone else just changed.
  highlightedFields: readonly LineField[];
  deliveryId: string;
  clientId?: string;
  setEditingLineId: (id: string | null) => void;
  setSelected: (id: string, selected: boolean) => void;
  onEditClick: (id: string) => void;
  onDeleteClick: (id: string) => void;
  rowActionsDisabled: boolean;
  updateLine: (id: string, line: any) => Promise<unknown>;
  updateSurchargeLine: (id: string, percentage: string) => Promise<unknown>;
  reorderLine: (lineId: string, toIndex: number) => Promise<unknown>;
  index: number;
  disabled: boolean;
  noCheckboxes: boolean;
  showStatusColumn: boolean;
}) => {
  const { t } = useTranslation(["invoicing", "components"]);
  const l10n = useL10n();
  const ref = useRef<HTMLDivElement>(null);
  const financeConfig = useSelector(selectFinanceConfiguration);

  const [hoverProp, setHoverProp] = useState<"ABOVE" | "BELOW" | null>(null);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: INVOICE_LINE,
      drop: (item: { line: any; index: number }, monitor) => {
        if (!ref.current) {
          return;
        }

        setHoverProp(null);

        const dragIndex = item.index;
        const hoverIndex = index;

        const clientOffset = monitor.getClientOffset();
        if (clientOffset === null) {
          return;
        }

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        const position = hoverClientY < hoverMiddleY ? "ABOVE" : "BELOW";

        const dropPosition = position === "ABOVE" ? hoverIndex : hoverIndex; // +1 but not working as should

        if (dragIndex === dropPosition) {
          return;
        }

        reorderLine(item.line.id, dropPosition);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover: (item, monitor) => {
        if (!ref.current || !monitor.isOver() || item.index === index) {
          setHoverProp(null);
          return;
        }

        const clientOffset = monitor.getClientOffset();
        if (clientOffset === null) {
          setHoverProp(null);
          return;
        }

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        const position = hoverClientY < hoverMiddleY ? "ABOVE" : "BELOW";

        if (position !== hoverProp) {
          setHoverProp(position);
        }
      },
      end: () => {
        setHoverProp(null);
      },
    }),
    [index, reorderLine]
  );

  const [{ isDragging }, drag] = useDrag(
    {
      type: INVOICE_LINE,
      item: { type: INVOICE_LINE, line, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: () => {
        setHoverProp(null);
      },
    },
    [index]
  );

  drag(drop(ref));

  const cellClass = (field: LineField, layout: string) =>
    clsx(layout, {
      "income-grid__cell--remote-change": highlightedFields.includes(field),
    });

  const classes = clsx("income-grid__row", "income-grid__row--draggable", {
    "income-grid__row--disabled": disabled,
  });

  return (
    <Fragment>
      {editing ? (
        <InvoiceLineEditingRow
          line={line}
          deliveryId={deliveryId}
          clientId={clientId}
          noCheckboxes={noCheckboxes}
          showStatusColumn={showStatusColumn}
          onUpdate={updateLine}
          onUpdateSurcharge={updateSurchargeLine}
          onCancel={() => setEditingLineId(null)}
          onAfterEdit={() => setEditingLineId(null)}
        />
      ) : (
        <div
          role="row"
          className={classes}
          ref={disabled ? null : ref}
          style={{
            opacity: isDragging ? 0.5 : 1,
            paddingBottom: hoverProp === "BELOW" && isOver ? "2rem" : "0",
            paddingTop: hoverProp === "ABOVE" && isOver ? "2rem" : "0",
            transition: "padding 0.2s ease-in-out", // Added transition for smooth animation
          }}
        >
          {!noCheckboxes && (
            <div
              role="gridcell"
              className="flex items-center income-grid-row__checkbox-cell"
            >
              <DoraCheckbox
                checked={selected}
                disabled={line.locked || line.deferredInvoiceInvoiced === false}
                onChange={() =>
                  !line.locked &&
                  line.deferredInvoiceInvoiced !== false &&
                  setSelected(line.id, !selected)
                }
              />
            </div>
          )}
          <div
            role="gridcell"
            className={cellClass(
              "productId",
              "flex items-center income-grid__grid-cell"
            )}
            aria-describedby="income-grid-heading-product"
          >
            {line.productId && <ProductCellValue productId={line.productId} />}
          </div>
          <div
            role="gridcell"
            className={cellClass(
              "description",
              "flex items-center income-grid__grid-cell"
            )}
          >
            {line.description}
          </div>
          <div
            role="gridcell"
            className={cellClass("quantity", "flex items-center justify-end")}
          >
            {l10n.formatNumber(line.quantity)}
          </div>
          <div
            role="gridcell"
            className={cellClass("unitId", "flex items-center")}
          >
            {line.unitId && <UnitCellValue unitId={line.unitId} />}
          </div>
          <div
            role="gridcell"
            className={cellClass("amount", "flex items-center justify-end")}
          >
            {l10n.formatCurrency(line.amount)}
          </div>
          <div
            role="gridcell"
            className={cellClass("percentage", "flex items-center justify-end")}
            aria-describedby="income-grid-heading-surcharge-percentage"
          >
            {line.percentage && l10n.formatPercentage(line.percentage)}
          </div>
          <div
            role="gridcell"
            className={cellClass(
              "discountPercentage",
              "flex items-center justify-end"
            )}
            aria-describedby="income-grid-heading-discount-percentage"
          >
            {line.discountPercentage &&
              `-${l10n.formatPercentage(line.discountPercentage)}`}
          </div>
          <div role="gridcell" className="flex items-center">
            {l10n.formatDate(line.date)}
          </div>
          <div role="gridcell" className="flex items-center justify-end">
            {l10n.formatCurrency(
              new Decimal(line.amount)
                .mul(new Decimal(line.quantity))
                .div(100)
                .mul(new Decimal(100).sub(line.discountPercentage || 0))
            )}
          </div>
          {showStatusColumn && (
            <div
              role="gridcell"
              className="flex items-center"
              style={{ color: "var(--gray-500)", fontSize: "0.75rem" }}
            >
              {line.locked
                ? line.draftInvoiceNumber
                  ? t("invoicing:draftInvoiceNumber", {
                      draftInvoiceNumber: line.draftInvoiceNumber,
                    })
                  : t("invoicing:invoiceLine.invoiced")
                : line.deferredInvoiceInvoiced === false
                  ? t("invoicing:invoiceLine.sentToConsolidated")
                  : ""}
            </div>
          )}
          <div role="gridcell" className="flex items-center justify-end">
            {line.locked ||
            disabled ||
            (line.deferredInvoiceInvoiced === false && !showStatusColumn) ? (
              <div
                className="flex gap-4"
                style={{ padding: 8, marginRight: 8 }}
              >
                <FontAwesomeIcon
                  style={{ color: "var(--gray-400)" }}
                  icon={faLock}
                />
                {financeConfig === "E-CONOMIC" ? (
                  <div style={{ width: 30, height: 30 }}>
                    {line.draftInvoiceNumber ? (
                      <Tooltip
                        title={t("invoicing:draftInvoiceNumber", {
                          draftInvoiceNumber: line.draftInvoiceNumber,
                        })}
                        placement="top"
                      >
                        <MarkEmailReadIcon
                          style={{ color: "var(--gray-400)", fontSize: 16 }}
                        />
                      </Tooltip>
                    ) : (
                      ""
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <DoraButton
                  variant="ghost"
                  className="income-grid__edit-line-button"
                  style={{ color: "var(--gray-400)" }}
                  onClick={(_) => onEditClick(line.id)}
                  disabled={line.isFromRoadToll || rowActionsDisabled}
                  aria-label={t("components:buttonLabels.edit")}
                  title={t("components:buttonLabels.edit")}
                >
                  <FontAwesomeIcon icon={faPencilAlt} />
                </DoraButton>
                <DoraButton
                  variant="ghost"
                  className="income-grid__delete-line-button"
                  style={{ color: "var(--gray-400)" }}
                  aria-label={t("components:buttonLabels.delete")}
                  title={t("components:buttonLabels.delete")}
                  onClick={() => onDeleteClick(line.id)}
                  disabled={rowActionsDisabled}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </DoraButton>
              </>
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default IncomeHandler;
